import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Calendar, Clock, Car, Wrench, User, Phone, Mail, CheckCircle2, AlertCircle, Loader2, FileText, PhoneCall, AlertTriangle, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { OptiQueueCalendar } from '@/components/booking/OptiQueueCalendar';

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 1989 + 2 }, (_, i) => currentYear + 1 - i);

type Step = 'loading' | 'unavailable' | 'vehicle' | 'lane' | 'services' | 'diagnostic' | 'request_notes' | 'preference' | 'calendar' | 'customer' | 'confirmed';
type BookingLane = 'maintenance' | 'diagnostic' | 'requested_work' | 'callback';

interface ShopInfo {
  shopId: string;
  shopName: string;
  enabled: boolean;
  settings?: {
    waiterAllowed: boolean;
    dropoffAllowed: boolean;
    diagnosticDefaultMinutes?: number;
  };
  lanes?: {
    maintenance: boolean;
    diagnostics: boolean;
    requestedWork: boolean;
    callback: boolean;
  };
  allowedJobs: { key: string; label: string; minutes: number; category: string }[];
  timezone: string;
  vehiclePackages?: string[];
}

interface TimeSlot {
  startAt: string;
  endAt: string;
  day: string;
  label: 'Best' | 'Good' | 'Limited';
  type: 'waiter' | 'dropoff';
  /** CORE-AR: opaque server token — the technician and bay are never sent to the customer. */
  slotToken?: string;
  notes?: string;
}

const DIAGNOSTIC_TYPES = [
  { key: 'check_engine', label: 'Check Engine Light', icon: AlertTriangle },
  { key: 'abs_light', label: 'ABS Light', icon: AlertTriangle },
  { key: 'airbag_light', label: 'Airbag Light', icon: AlertTriangle },
  { key: 'tpms_light', label: 'TPMS Light', icon: AlertTriangle },
  { key: 'brake_light', label: 'Brake Light', icon: AlertTriangle },
  { key: 'battery_light', label: 'Battery Light', icon: AlertTriangle },
  { key: 'noise_vibration', label: 'Noise / Vibration', icon: AlertCircle },
  { key: 'electrical', label: 'Electrical Issue', icon: AlertCircle },
  { key: 'general', label: 'General Concern', icon: AlertCircle },
];

const CATEGORY_LABELS: Record<string, string> = {
  maintenance: 'Maintenance',
  inspection: 'Inspection',
  inspections: 'Inspection',
  tires: 'Tires',
};

interface ServicesAccordionProps {
  jobs: { key: string; label: string; minutes: number; category: string }[];
  selectedJobs: string[];
  onSelectionChange: (jobs: string[]) => void;
  onBack: () => void;
  onNext: () => void;
}

function ServicesAccordion({ jobs, selectedJobs, onSelectionChange, onBack, onNext }: ServicesAccordionProps) {
  // Group jobs by category and sort alphabetically within each
  const jobsByCategory = useMemo(() => {
    const grouped: Record<string, typeof jobs> = {};
    for (const job of jobs) {
      // Normalize inspection/inspections to same key
      const categoryKey = job.category === 'inspections' ? 'inspection' : job.category;
      if (!grouped[categoryKey]) {
        grouped[categoryKey] = [];
      }
      grouped[categoryKey].push(job);
    }
    // Sort jobs alphabetically within each category
    for (const key of Object.keys(grouped)) {
      grouped[key].sort((a, b) => a.label.localeCompare(b.label));
    }
    return grouped;
  }, [jobs]);

  // Track which categories are expanded (all collapsed by default)
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});

  const toggleCategory = (category: string) => {
    setOpenCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleJobToggle = (jobKey: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedJobs, jobKey]);
    } else {
      onSelectionChange(selectedJobs.filter(j => j !== jobKey));
    }
  };

  const categoryOrder = ['maintenance', 'inspection', 'tires'];
  const sortedCategories = Object.keys(jobsByCategory).sort(
    (a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b)
  );

  return (
    <>
      <div className="space-y-3">
        {sortedCategories.map(category => {
          const categoryJobs = jobsByCategory[category];
          const selectedCount = categoryJobs.filter(j => selectedJobs.includes(j.key)).length;
          
          return (
            <Collapsible
              key={category}
              open={openCategories[category]}
              onOpenChange={() => toggleCategory(category)}
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{CATEGORY_LABELS[category] || category}</span>
                  {selectedCount > 0 && (
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                      {selectedCount} selected
                    </span>
                  )}
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform ${openCategories[category] ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-1 space-y-1 pl-4">
                {categoryJobs.map(job => (
                  <label 
                    key={job.key} 
                    className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50"
                  >
                    <Checkbox
                      checked={selectedJobs.includes(job.key)}
                      onCheckedChange={(checked) => handleJobToggle(job.key, !!checked)}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{job.label}</div>
                      <div className="text-sm text-muted-foreground">~{job.minutes} min</div>
                    </div>
                  </label>
                ))}
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button onClick={onNext} className="flex-1">Continue</Button>
      </div>
    </>
  );
}

export default function OptiQueueBookingPage() {
  const { shopSlug } = useParams<{ shopSlug: string }>();
  const shopSlugNormalized = (shopSlug ?? '').trim().toLowerCase();

  const [step, setStep] = useState<Step>('loading');
  const [shopInfo, setShopInfo] = useState<ShopInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  
  // Vehicle dropdown state
  const [availableMakes, setAvailableMakes] = useState<string[]>([]);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [isLoadingMakes, setIsLoadingMakes] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [selectedLane, setSelectedLane] = useState<BookingLane | null>(null);
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [diagnosticIntent, setDiagnosticIntent] = useState('general');
  const [customerRequestNotes, setCustomerRequestNotes] = useState('');
  const [preference, setPreference] = useState<'either' | 'waiter' | 'dropoff'>('either');
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [bookingRequestId, setBookingRequestId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [confirmedTicketId, setConfirmedTicketId] = useState<string | null>(null);
  const [confirmationRequired, setConfirmationRequired] = useState(false);

  // Load shop info on mount
  useEffect(() => {
    if (!shopSlugNormalized) return;

    // Prevent accidental testing on the literal route pattern: /book/:shopSlug
    if (shopSlugNormalized.includes(':') || shopSlugNormalized === 'shopslug') {
      setError('Invalid booking link. Please use a real shop link like /book/optibayhq-1c8914a1');
      setStep('unavailable');
      return;
    }

    async function loadShopInfo() {
      try {
        console.log('[OPTIQUEUE][BOOKING] Loading shop info for slug:', shopSlugNormalized);

        const { data, error } = await supabase.functions.invoke('optiqueue-shop-info', {
          body: { shopSlug: shopSlugNormalized },
        });

        if (error) {
          console.error('[OPTIQUEUE][BOOKING] Shop info invoke error:', error);
          setError(error.message || 'Failed to load shop');
          setStep('unavailable');
          return;
        }

        if (!data?.enabled) {
          setError(data?.error || null);
          setStep('unavailable');
          return;
        }

        setError(null);
        setShopInfo(data);
        setStep('vehicle');
      } catch (err) {
        console.error('[OPTIQUEUE][BOOKING] Failed to load shop info:', err);
        setError('Failed to load shop');
        setStep('unavailable');
      }
    }

    loadShopInfo();
  }, [shopSlugNormalized]);

  // Fetch makes when year changes
  useEffect(() => {
    if (!vehicleYear || !shopInfo?.vehiclePackages?.length) {
      setAvailableMakes([]);
      return;
    }

    async function fetchMakes() {
      setIsLoadingMakes(true);
      setAvailableMakes([]);
      setVehicleMake('');
      setAvailableModels([]);
      setVehicleModel('');

      try {
        const { data, error } = await supabase.functions.invoke('get-vehicle-makes-catalog', {
          body: { year: parseInt(vehicleYear), packageTypes: shopInfo.vehiclePackages }
        });

        if (!error && data?.makes) {
          setAvailableMakes(data.makes);
        }
      } catch (err) {
        console.error('[OPTIQUEUE] Failed to fetch makes:', err);
      } finally {
        setIsLoadingMakes(false);
      }
    }

    fetchMakes();
  }, [vehicleYear, shopInfo?.vehiclePackages]);

  // Fetch models when make changes
  useEffect(() => {
    if (!vehicleYear || !vehicleMake || !shopInfo?.vehiclePackages?.length) {
      setAvailableModels([]);
      return;
    }

    async function fetchModels() {
      setIsLoadingModels(true);
      setAvailableModels([]);
      setVehicleModel('');

      try {
        const { data, error } = await supabase.functions.invoke('get-vehicle-models-catalog', {
          body: { year: parseInt(vehicleYear), make: vehicleMake, packageTypes: shopInfo.vehiclePackages }
        });

        if (!error && data?.models) {
          setAvailableModels(data.models);
        }
      } catch (err) {
        console.error('[OPTIQUEUE] Failed to fetch models:', err);
      } finally {
        setIsLoadingModels(false);
      }
    }

    fetchModels();
  }, [vehicleYear, vehicleMake, shopInfo?.vehiclePackages]);

  const handleVehicleNext = () => {
    if (!vehicleYear || !vehicleMake || !vehicleModel) {
      setError('Please fill in all vehicle fields');
      return;
    }
    setError(null);
    setStep('lane');
  };

  const handleLaneSelect = (lane: BookingLane) => {
    setSelectedLane(lane);
    setError(null);

    switch (lane) {
      case 'maintenance':
        setStep('services');
        break;
      case 'diagnostic':
        setStep('diagnostic');
        break;
      case 'requested_work':
        setStep('request_notes');
        break;
      case 'callback':
        // For callback, skip to preference/customer info
        setStep('preference');
        break;
    }
  };

  const handleServicesNext = () => {
    if (selectedJobs.length === 0) {
      setError('Please select at least one service');
      return;
    }
    setError(null);
    setStep('preference');
  };

  const handleDiagnosticNext = () => {
    setError(null);
    setStep('preference');
  };

  const handleRequestNotesNext = () => {
    if (!customerRequestNotes.trim()) {
      setError('Please describe the work you were advised about');
      return;
    }
    setError(null);
    setStep('preference');
  };

  const handlePreferenceNext = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Create preview with lane info
      const { data: previewData, error: previewError } = await supabase.functions.invoke('optiqueue-create-preview', {
        body: {
          shopSlug: shopSlugNormalized,
          vehicleYear: parseInt(vehicleYear),
          vehicleMake,
          vehicleModel,
          requestedJobKeys: selectedLane === 'maintenance' ? selectedJobs : undefined,
          diagnosticIntent: selectedLane === 'diagnostic' ? diagnosticIntent : undefined,
          customerRequestNotes: ['requested_work', 'callback'].includes(selectedLane || '') ? customerRequestNotes : undefined,
          preference,
          lane: selectedLane
        }
      });

      if (previewError) throw previewError;
      setBookingRequestId(previewData.bookingRequestId);

      // Go to calendar step instead of fetching all times
      setStep('calendar');
    } catch (err: any) {
      setError(err.message || 'Failed to proceed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalendarComplete = async (date: string, slot?: TimeSlot) => {
    setIsLoading(true);
    setError(null);

    try {
      if (slot) {
        // Waiter with specific time - use existing hold logic
        setSelectedSlot(slot);
        const { data, error } = await supabase.functions.invoke('optiqueue-hold', {
          body: {
            bookingRequestId,
            slotToken: slot.slotToken
          }
        });
        if (error) {
          // Try to extract real error message from response context
          const errorMsg = await extractEdgeFunctionError(error);
          throw new Error(errorMsg);
        }
      } else {
        // Drop-off with date only - call new endpoint
        const { data, error } = await supabase.functions.invoke('optiqueue-hold-dropoff', {
          body: { bookingRequestId, date }
        });
        
        if (error) {
          const errorMsg = await extractEdgeFunctionError(error);
          throw new Error(errorMsg);
        }
        if (data?.error) {
          throw new Error(data.error);
        }
        
        setSelectedSlot(data.slot);
      }
      setStep('customer');
    } catch (err: any) {
      console.error('[OptiQueueBookingPage] Hold error:', err);
      const msg = err.message || 'Failed to hold slot';
      if (msg.includes('SLOT_TAKEN')) {
        setError('This slot was just taken. Please select another.');
      } else if (msg.includes('No available slots')) {
        setError('No available slots on this day. Please try another date.');
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to extract error message from edge function error context
  const extractEdgeFunctionError = async (error: any): Promise<string> => {
    try {
      // Check if error has a context with a Response object
      if (error?.context && typeof error.context.json === 'function') {
        const body = await error.context.json();
        if (body?.error) return body.error;
      }
      // Fall back to message
      return error?.message || 'Request failed';
    } catch {
      return error?.message || 'Request failed';
    }
  };

  const handleSlotSelect = async (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke('optiqueue-hold', {
        body: {
          bookingRequestId,
          slotToken: slot.slotToken
        }
      });

      if (error) throw error;
      setStep('customer');
    } catch (err: any) {
      if (err.message?.includes('SLOT_TAKEN')) {
        setError('This time slot was just taken. Please select another.');
      } else {
        setError(err.message || 'Failed to hold time slot');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!customerName || !customerPhone) {
      setError('Name and phone are required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke('optiqueue-confirm', {
        body: {
          bookingRequestId,
          customerName,
          customerPhone,
          customerEmail,
          notes
        }
      });

      if (error) throw error;

      setConfirmedTicketId(data.ticketId);
      setConfirmationRequired(data.confirmationRequired || false);
      setStep('confirmed');
    } catch (err: any) {
      if (err.message?.includes('HOLD_EXPIRED')) {
        setError('Your hold expired. Please select a new time slot.');
        setStep('calendar');
      } else {
        setError(err.message || 'Failed to confirm booking');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (step === 'unavailable') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <CardTitle>Online Booking Unavailable</CardTitle>
            <CardDescription>
              {error
                ? error
                : 'Online booking is not currently available for this shop. Please call to schedule an appointment.'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (step === 'confirmed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            {confirmationRequired ? (
              <Phone className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            ) : (
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            )}
            <CardTitle>
              {confirmationRequired ? 'Request Received!' : 'Appointment Confirmed!'}
            </CardTitle>
            <CardDescription>
              {confirmationRequired
                ? `${shopInfo?.shopName} will contact you shortly to confirm your appointment details.`
                : `Your appointment at ${shopInfo?.shopName} has been booked.`
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedSlot && (
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{format(new Date(selectedSlot.startAt), 'EEEE, MMMM d, yyyy')}</span>
                </div>
                {preference !== 'dropoff' && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>{format(new Date(selectedSlot.startAt), 'h:mm a')}</span>
                  </div>
                )}
                {preference === 'dropoff' && (
                  <div className="flex items-center gap-2">
                    <Car className="h-4 w-4" />
                    <span>Drop off during business hours</span>
                  </div>
                )}
              </div>
            )}
            {confirmationRequired && (
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  <strong>Note:</strong> Your appointment is pending confirmation. The shop will contact you within 24 hours to confirm the work, timing, and finalize your appointment.
                </p>
              </div>
            )}
            <p className="text-sm text-muted-foreground text-center">
              Reference #{confirmedTicketId}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStepDescription = () => {
    switch (step) {
      case 'vehicle': return 'Step 1: Vehicle Information';
      case 'lane': return 'Step 2: What brings you in?';
      case 'services': return 'Step 3: Select Services';
      case 'diagnostic': return 'Step 3: What\'s the concern?';
      case 'request_notes': return 'Step 3: Describe the work';
      case 'preference': return `Step ${selectedLane === 'callback' ? 3 : 4}: Appointment Type`;
      case 'calendar': return `Step ${selectedLane === 'callback' ? 4 : 5}: Choose a Date`;
      case 'customer': return `Step ${selectedLane === 'callback' ? 5 : 6}: Your Information`;
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-lg mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {shopInfo?.shopName || 'Book Appointment'}
            </CardTitle>
            <CardDescription>{getStepDescription()}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {error && (
              <div className="bg-destructive/10 text-destructive p-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Vehicle Step */}
            {step === 'vehicle' && (
              <>
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Select
                    value={vehicleYear}
                    onValueChange={(value) => setVehicleYear(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {YEARS.map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Make</Label>
                  <Select
                    value={vehicleMake}
                    onValueChange={(value) => setVehicleMake(value)}
                    disabled={!vehicleYear || isLoadingMakes}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={isLoadingMakes ? "Loading..." : "Select make"} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableMakes.map(make => (
                        <SelectItem key={make} value={make}>{make}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select
                    value={vehicleModel}
                    onValueChange={(value) => setVehicleModel(value)}
                    disabled={!vehicleMake || isLoadingModels}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={isLoadingModels ? "Loading..." : "Select model"} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableModels.map(model => (
                        <SelectItem key={model} value={model}>{model}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleVehicleNext} className="w-full" disabled={!vehicleYear || !vehicleMake || !vehicleModel}>
                  Continue
                </Button>
              </>
            )}

            {/* Lane Selection Step */}
            {step === 'lane' && (
              <>
                <div className="space-y-3">
                  {/* Lane 1: Maintenance */}
                  {shopInfo?.lanes?.maintenance && (
                    <button
                      onClick={() => handleLaneSelect('maintenance')}
                      className="w-full text-left p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                          <Wrench className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium">Maintenance & Quick Services</div>
                          <div className="text-sm text-muted-foreground">Oil change, tire rotation, inspections, etc.</div>
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Lane 2: Diagnostics */}
                  {shopInfo?.lanes?.diagnostics && (
                    <button
                      onClick={() => handleLaneSelect('diagnostic')}
                      className="w-full text-left p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                          <AlertTriangle className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div>
                          <div className="font-medium">Something's Wrong</div>
                          <div className="text-sm text-muted-foreground">Check engine light, noises, electrical issues</div>
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Lane 3A: Previously Recommended */}
                  {shopInfo?.lanes?.requestedWork && (
                    <button
                      onClick={() => handleLaneSelect('requested_work')}
                      className="w-full text-left p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium">Schedule Work I Was Advised About</div>
                          <div className="text-sm text-muted-foreground">We'll confirm details before locking in your appointment</div>
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Lane 3B: Callback */}
                  {shopInfo?.lanes?.callback && (
                    <button
                      onClick={() => handleLaneSelect('callback')}
                      className="w-full text-left p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                          <PhoneCall className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <div className="font-medium">Not Sure — Please Call Me</div>
                          <div className="text-sm text-muted-foreground">We'll contact you to discuss your needs</div>
                        </div>
                      </div>
                    </button>
                  )}
                </div>
                <Button variant="outline" onClick={() => setStep('vehicle')}>Back</Button>
              </>
            )}

            {/* Services Step (Lane 1) */}
            {step === 'services' && (
              <ServicesAccordion
                jobs={shopInfo?.allowedJobs || []}
                selectedJobs={selectedJobs}
                onSelectionChange={setSelectedJobs}
                onBack={() => setStep('lane')}
                onNext={handleServicesNext}
              />
            )}

            {/* Diagnostic Type Step (Lane 2) */}
            {step === 'diagnostic' && (
              <>
                <RadioGroup value={diagnosticIntent} onValueChange={setDiagnosticIntent}>
                  {DIAGNOSTIC_TYPES.map(type => (
                    <label key={type.key} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50">
                      <RadioGroupItem value={type.key} />
                      <type.icon className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium">{type.label}</span>
                    </label>
                  ))}
                </RadioGroup>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep('lane')}>Back</Button>
                  <Button onClick={handleDiagnosticNext} className="flex-1">Continue</Button>
                </div>
              </>
            )}

            {/* Request Notes Step (Lane 3A) */}
            {step === 'request_notes' && (
              <>
                <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg mb-4">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    <strong>Important:</strong> Because labor times vary by vehicle and condition, the shop will contact you shortly to confirm the exact repair, timing, and appointment details. Your appointment is not locked in until confirmed.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Describe the work you were advised about</Label>
                  <Textarea 
                    placeholder="e.g., You recommended rear brakes last visit, Need the transmission flush discussed, Suspension work from my estimate..."
                    value={customerRequestNotes}
                    onChange={e => setCustomerRequestNotes(e.target.value)}
                    rows={4}
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep('lane')}>Back</Button>
                  <Button onClick={handleRequestNotesNext} className="flex-1">Continue</Button>
                </div>
              </>
            )}

            {/* Preference Step */}
            {step === 'preference' && (
              <>
                <RadioGroup value={preference} onValueChange={(v) => setPreference(v as typeof preference)}>
                  {shopInfo?.settings?.waiterAllowed && (
                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50">
                      <RadioGroupItem value="waiter" />
                      <div>
                        <div className="font-medium">Waiter</div>
                        <div className="text-sm text-muted-foreground">Wait while we work on your vehicle</div>
                      </div>
                    </label>
                  )}
                  {shopInfo?.settings?.dropoffAllowed && (
                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50">
                      <RadioGroupItem value="dropoff" />
                      <div>
                        <div className="font-medium">Drop-off</div>
                        <div className="text-sm text-muted-foreground">Leave your vehicle and pick up later</div>
                      </div>
                    </label>
                  )}
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50">
                    <RadioGroupItem value="either" />
                    <div>
                      <div className="font-medium">Either</div>
                      <div className="text-sm text-muted-foreground">Show me all available times</div>
                    </div>
                  </label>
                </RadioGroup>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => {
                    switch (selectedLane) {
                      case 'maintenance': setStep('services'); break;
                      case 'diagnostic': setStep('diagnostic'); break;
                      case 'requested_work': setStep('request_notes'); break;
                      case 'callback': setStep('lane'); break;
                      default: setStep('lane');
                    }
                  }}>Back</Button>
                  <Button onClick={handlePreferenceNext} disabled={isLoading} className="flex-1">
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Find Times'}
                  </Button>
                </div>
              </>
            )}

            {/* Calendar Step */}
            {step === 'calendar' && bookingRequestId && (
              <OptiQueueCalendar
                bookingRequestId={bookingRequestId}
                preference={preference === 'either' ? 'waiter' : preference}
                shopTimezone={shopInfo?.timezone || 'America/Chicago'}
                onComplete={handleCalendarComplete}
                onBack={() => setStep('preference')}
              />
            )}


            {/* Customer Info Step */}
            {step === 'customer' && (
              <>
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input 
                    placeholder="John Smith" 
                    value={customerName}
                    onChange={e => setCustomerName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input 
                    type="tel"
                    placeholder="(555) 123-4567" 
                    value={customerPhone}
                    onChange={e => setCustomerPhone(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    placeholder="john@example.com" 
                    value={customerEmail}
                    onChange={e => setCustomerEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea 
                    placeholder="Any special requests..." 
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep('calendar')}>Back</Button>
                  <Button onClick={handleConfirm} disabled={isLoading} className="flex-1">
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 
                      (selectedLane === 'requested_work' || selectedLane === 'callback') 
                        ? 'Submit Request' 
                        : 'Confirm Appointment'
                    }
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
